// 2026-06-25: Phase 12 — Operation Master.
// Defines the operation types each shop can do (DRILLING, MILLING, ...).
// Stored in TB customer attribute 'operation_master'.
import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Tooltip, IconButton, Dialog, DialogTitle, TextField, Button, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { customerbasedshift, cleanCustomerId } from '../../Services/app/operatorservice';
import { shiftadd } from '../../Services/app/masterservice';
import Swal from 'sweetalert2';
import './operationmaster.css';

// 25 default operations across 7 shops, seeded on first visit.
const DEFAULT_OPERATIONS = [
  // MACHINE_SHOP
  { code: 'MILLING',       name: 'Milling',          shop_type: 'MACHINE_SHOP',    color: '#2563eb', default_setup_min: 30, sort_order: 1 },
  { code: 'TURNING',       name: 'Turning',          shop_type: 'MACHINE_SHOP',    color: '#3b82f6', default_setup_min: 25, sort_order: 2 },
  { code: 'DRILLING',      name: 'Drilling',         shop_type: 'MACHINE_SHOP',    color: '#60a5fa', default_setup_min: 15, sort_order: 3 },
  { code: 'TAPPING',       name: 'Tapping',          shop_type: 'MACHINE_SHOP',    color: '#1d4ed8', default_setup_min: 20, sort_order: 4 },
  { code: 'BORING',        name: 'Boring',           shop_type: 'MACHINE_SHOP',    color: '#1e40af', default_setup_min: 30, sort_order: 5 },
  { code: 'GRINDING',      name: 'Grinding',         shop_type: 'MACHINE_SHOP',    color: '#312e81', default_setup_min: 40, sort_order: 6 },
  { code: 'REAMING',       name: 'Reaming',          shop_type: 'MACHINE_SHOP',    color: '#6366f1', default_setup_min: 15, sort_order: 7 },

  // DEBURRING
  { code: 'MANUAL_DEBURR', name: 'Manual Deburring', shop_type: 'DEBURRING',       color: '#f59e0b', default_setup_min: 5,  sort_order: 1 },
  { code: 'BRUSH_DEBURR',  name: 'Brush Deburring',  shop_type: 'DEBURRING',       color: '#fbbf24', default_setup_min: 10, sort_order: 2 },
  { code: 'TUMBLING',      name: 'Tumbling',         shop_type: 'DEBURRING',       color: '#d97706', default_setup_min: 15, sort_order: 3 },

  // SPECIAL_PROCESS
  { code: 'HEAT_TREAT',    name: 'Heat Treatment',   shop_type: 'SPECIAL_PROCESS', color: '#dc2626', default_setup_min: 60, sort_order: 1 },
  { code: 'PLATING',       name: 'Plating',          shop_type: 'SPECIAL_PROCESS', color: '#ef4444', default_setup_min: 45, sort_order: 2 },
  { code: 'ANODIZING',     name: 'Anodizing',        shop_type: 'SPECIAL_PROCESS', color: '#b91c1c', default_setup_min: 45, sort_order: 3 },
  { code: 'BLACK_OXIDE',   name: 'Black Oxide',      shop_type: 'SPECIAL_PROCESS', color: '#991b1b', default_setup_min: 30, sort_order: 4 },
  { code: 'PAINTING',      name: 'Painting',         shop_type: 'SPECIAL_PROCESS', color: '#f87171', default_setup_min: 30, sort_order: 5 },

  // ASSEMBLY
  { code: 'BOLT_ASSY',     name: 'Bolt Assembly',    shop_type: 'ASSEMBLY',        color: '#16a34a', default_setup_min: 10, sort_order: 1 },
  { code: 'PRESS_FIT',     name: 'Press Fit',        shop_type: 'ASSEMBLY',        color: '#15803d', default_setup_min: 15, sort_order: 2 },
  { code: 'ADHESIVE',      name: 'Adhesive Bond',    shop_type: 'ASSEMBLY',        color: '#22c55e', default_setup_min: 10, sort_order: 3 },
  { code: 'RIVETING',      name: 'Riveting',         shop_type: 'ASSEMBLY',        color: '#166534', default_setup_min: 10, sort_order: 4 },
  { code: 'WELDING',       name: 'Welding',          shop_type: 'ASSEMBLY',        color: '#14532d', default_setup_min: 20, sort_order: 5 },

  // INSPECTION
  { code: 'CMM',           name: 'CMM Inspection',   shop_type: 'INSPECTION',      color: '#9333ea', default_setup_min: 20, sort_order: 1 },
  { code: 'GAUGING',       name: 'Gauging',          shop_type: 'INSPECTION',      color: '#a855f7', default_setup_min: 5,  sort_order: 2 },
  { code: 'VISUAL_INSP',   name: 'Visual Inspection',shop_type: 'INSPECTION',      color: '#c084fc', default_setup_min: 5,  sort_order: 3 },
  { code: 'FUNCTIONAL_TEST',name:'Functional Test',  shop_type: 'INSPECTION',      color: '#7e22ce', default_setup_min: 25, sort_order: 4 },
  { code: 'HARDNESS_TEST', name: 'Hardness Test',    shop_type: 'INSPECTION',      color: '#6b21a8', default_setup_min: 10, sort_order: 5 },

  // PACKING
  { code: 'BOX_PACK',      name: 'Box Packing',      shop_type: 'PACKING',         color: '#6b7280', default_setup_min: 10, sort_order: 1 },
  { code: 'PALLET_PACK',   name: 'Pallet Packing',   shop_type: 'PACKING',         color: '#4b5563', default_setup_min: 20, sort_order: 2 },
  { code: 'SHRINK_WRAP',   name: 'Shrink Wrap',      shop_type: 'PACKING',         color: '#374151', default_setup_min: 10, sort_order: 3 },
];

const OperationMaster = () => {
  const customerId = cleanCustomerId(localStorage.getItem('CustomerID'));
  const [operations, setOperations] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [shopFilter, setShopFilter] = useState(searchParams.get('shop') || 'ALL');
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [ops, procs] = await Promise.all([
        customerbasedshift(customerId, 'operation_master'),
        customerbasedshift(customerId, 'process_master').catch(() => []),
      ]);
      setProcesses(Array.isArray(procs?.[0]?.value) ? procs[0].value : []);
      const all = ops?.[0]?.value || [];
      if (Array.isArray(all) && all.length > 0) {
        setOperations(all);
      } else {
        const now = Date.now();
        const seeded = DEFAULT_OPERATIONS.map((o, i) => ({
          id: `op-${now}-${i}`, ...o,
          created_at: now, updated_at: now, active: true,
        }));
        await shiftadd({ operation_master: seeded }, customerId, 'SERVER_SCOPE');
        setOperations(seeded);
      }
    } catch (e) { console.error(e); setOperations([]); }
  };

  const filtered = useMemo(() => {
    let f = operations;
    if (shopFilter !== 'ALL') f = f.filter((o) => o.shop_type === shopFilter);
    return f.slice().sort((a, b) =>
      (a.shop_type || '').localeCompare(b.shop_type || '') ||
      (a.sort_order || 99) - (b.sort_order || 99)
    );
  }, [operations, shopFilter]);

  const shopOptions = useMemo(() => {
    const codes = new Set(operations.map((o) => o.shop_type));
    return ['ALL', ...Array.from(codes)];
  }, [operations]);

  const handleDelete = (op) => {
    Swal.fire({
      title: `Delete operation "${op.name}"?`,
      icon: 'warning', showCancelButton: true, confirmButtonColor: '#dc2626',
    }).then(async (r) => {
      if (!r.isConfirmed) return;
      const updated = operations.filter((p) => p.id !== op.id);
      await shiftadd({ operation_master: updated }, customerId, 'SERVER_SCOPE');
      fetchAll();
    });
  };

  return (
    <div className="pages opmaster-page" style={{ paddingBlockStart: 40 }}>
      <div className="pagecontents">
        <div className="left-labels" style={{ padding: '1rem 2rem 1rem 0' }}>
          <div className="shift-content-1">
            {shopFilter !== 'ALL' && (
              <button onClick={() => navigate('/process-master')}
                      style={{ background: 'none', border: 'none', color: '#f97316', cursor: 'pointer', fontSize: 12, fontWeight: 700, marginBottom: 6, padding: 0 }}>
                ← Back to Process Master
              </button>
            )}
            <h5>Operation Master{shopFilter !== 'ALL' && ` — ${shopFilter}`}</h5>
            <div className="add_new">
              <Tooltip title="Add Operation">
                <IconButton className="circle" onClick={() => setIsAddOpen(true)}><AddIcon /></IconButton>
              </Tooltip>
            </div>
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
            Define the operations each shop can perform (Drilling, Milling, Tapping, etc.).
            Used by Work Center Capabilities + Scheduler to ensure ops only run on capable machines.
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, padding: '6px 0 14px 0' }}>
          {shopOptions.map((s) => (
            <button key={s}
                    className={`opmaster-tab ${shopFilter === s ? 'opmaster-tab-active' : ''}`}
                    onClick={() => setShopFilter(s)}>
              {s === 'ALL' ? 'All Shops' : s}
            </button>
          ))}
        </div>

        <div className="op-list">
          {filtered.length === 0 ? (
            <div style={{ margin: '2rem', color: '#888' }}>No operations defined for this filter.</div>
          ) : filtered.map((o) => {
            const proc = processes.find((p) => p.code === o.shop_type);
            return (
              <div className="op-item" key={o.id} style={{ borderLeftColor: o.color || '#9ca3af' }}>
                <div className="op-info">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="op-shop-pill" style={{ background: proc?.color || '#6b7280' }}>{o.shop_type}</span>
                    <strong>{o.name}</strong>
                    <span className="op-code">{o.code}</span>
                  </div>
                  <div className="op-meta">
                    Default setup: <strong>{o.default_setup_min} min</strong>
                  </div>
                </div>
                <div className="op-actions">
                  <Tooltip title="Edit"><IconButton size="small" onClick={() => setEditData(o)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Delete"><IconButton size="small" onClick={() => handleDelete(o)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {isAddOpen && (
        <OpDialog
          mode="add"
          processes={processes}
          existing={operations}
          onClose={() => setIsAddOpen(false)}
          onSaved={() => { setIsAddOpen(false); fetchAll(); }}
          customerId={customerId}
        />
      )}
      {editData && (
        <OpDialog
          mode="edit"
          data={editData}
          processes={processes}
          existing={operations}
          onClose={() => setEditData(null)}
          onSaved={() => { setEditData(null); fetchAll(); }}
          customerId={customerId}
        />
      )}
    </div>
  );
};

function OpDialog({ mode, data, processes, existing, onClose, onSaved, customerId }) {
  const [form, setForm] = useState(data || {
    code: '', name: '', shop_type: processes[0]?.code || 'MACHINE_SHOP',
    color: '#2563eb', default_setup_min: 15, sort_order: existing.length + 1, active: true,
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const code = (form.code || '').trim().toUpperCase().replace(/\s+/g, '_');
    if (!code) { Swal.fire('Missing', 'Code required (e.g. WELDING)', 'warning'); return; }
    if (!form.name?.trim()) { Swal.fire('Missing', 'Name required', 'warning'); return; }
    if (mode === 'add' && existing.some((o) => o.code === code)) {
      Swal.fire('Duplicate', `Operation "${code}" already exists`, 'error'); return;
    }
    setSaving(true);
    try {
      const now = Date.now();
      let updated;
      if (mode === 'add') {
        const item = {
          id: `op-${now}-${Math.floor(Math.random() * 9999)}`,
          code, name: form.name.trim(), shop_type: form.shop_type,
          color: form.color, default_setup_min: Number(form.default_setup_min) || 15,
          sort_order: Number(form.sort_order) || existing.length + 1,
          active: true, created_at: now, updated_at: now,
        };
        updated = [...existing, item];
      } else {
        updated = existing.map((o) => o.id === form.id ? { ...form, updated_at: now } : o);
      }
      await shiftadd({ operation_master: updated }, customerId, 'SERVER_SCOPE');
      Swal.fire({ icon: 'success', title: mode === 'add' ? 'Operation added' : 'Saved', timer: 1100, showConfirmButton: false });
      onSaved();
    } catch (e) {
      Swal.fire('Error', e?.response?.data?.message || e.message, 'error');
    }
    setSaving(false);
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle style={{ display: 'flex', justifyContent: 'space-between' }}>
        {mode === 'add' ? 'Add Operation' : `Edit Operation — ${form.code}`}
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <div style={{ padding: '12px 24px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <TextField size="small" label="Code *" value={form.code} disabled={mode === 'edit'}
                    onChange={(e) => setForm({ ...form, code: e.target.value })}
                    placeholder="WELDING / GRINDING / etc."
                    helperText={mode === 'edit' ? 'Code is locked to preserve references' : 'UPPERCASE_WITH_UNDERSCORES'} />
        <TextField size="small" label="Display name *" value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <FormControl size="small">
          <InputLabel>Shop *</InputLabel>
          <Select value={form.shop_type} onChange={(e) => setForm({ ...form, shop_type: e.target.value })} label="Shop *">
            {processes.map((p) => <MenuItem key={p.code} value={p.code}>{p.name}</MenuItem>)}
          </Select>
        </FormControl>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <TextField size="small" label="Default setup (min)" type="number" value={form.default_setup_min}
                      onChange={(e) => setForm({ ...form, default_setup_min: e.target.value })} />
          <TextField size="small" label="Color" type="color" value={form.color}
                      onChange={(e) => setForm({ ...form, color: e.target.value })} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onClose} variant="outlined" color="inherit">Cancel</Button>
          <Button onClick={submit} variant="contained" disabled={saving} style={{ background: '#f97316' }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

export default OperationMaster;
